import { Injectable } from '@angular/core';
import {
	addressString,
	getRegionName,
	getRegionType,
} from '@core/util/address';
import { clientLegalName, isGuarantor } from '@core/util/client';
import { formatCentsToDollars } from '@core/util/money';
import { listParcelsPin } from '@core/util/parcels';
import { joinNames, splitSentenceByCharactersLimit } from '@core/util/string';
import { Business, BusinessAddress, BusinessUser } from '@models/businesses';
import { AddressFormat } from '@models/businessSettings/businessFormatTypes';
import {
	AddressFormattingSettings,
	NameFormattingSettings,
} from '@models/businessSettings/settings';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	LTTRate,
	MatterTitle,
	ClientsCapacity,
	MatterFileType,
} from '@models/matter';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../base-form-mapper';
import { MatterMortgageWithAssociations } from '@models/mortgage';
import { Jurisdiction } from '@models/jurisdictions';
import { MapAddress } from '@state/global/tags/mappings/tags.address';
import { getLTTLegalDescription } from '@core/util/legalDescription';
import { getBorrowerServiceAddress } from '@core/util/borrowers';

@Injectable({
	providedIn: 'root',
})
export class MLTT_AFFIDAVIT_FORM_Mapper extends BaseFormMapper<MLTT_AFFIDAVIT_FORM> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: MLTT_AFFIDAVIT_FORM,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage?: MatterMortgageWithAssociations,
		businessUsers?: BusinessUser[],
		primaryJurisdiction?: Jurisdiction,
		signors?: PersonMatterClientWithAssociations[],
		otherPartiesWithAssociations?: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		matterFileType?: MatterFileType,
		addressFormattingSettings?: AddressFormattingSettings,
	) {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as MLTT_AFFIDAVIT_FORM);

		const FilteredMatterClients =
			matterClients?.filter((client) => !isGuarantor(client)) ?? [];

		const propertyAddress = MapAddress(
			matter?.propertyDetails?.address,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		const legalDescription = getLTTLegalDescription(
			matterTitles,
			matter?.propertyDetails?.propertyType?.type,
			primaryJurisdiction,
			propertyAddress,
		);

		const borrowerServiceAddress = getBorrowerServiceAddress(
			matterFileType,
			signors,
			otherPartiesWithAssociations,
			addressFormattingSettings || { format: AddressFormat.FORMAT_1 },
		);

		if (legalDescription) {
			const ConvenyanceOf1CharacterLimit = 54;
			const ConvenyanceOf2And3CharacterLimit = 110;

			const FirstSplittedDescription = splitSentenceByCharactersLimit(
				legalDescription,
				ConvenyanceOf1CharacterLimit,
			);
			const splittedDescription = [FirstSplittedDescription.shift()];
			let SecondSplittedDescription = [];

			if (FirstSplittedDescription.length > 0) {
				SecondSplittedDescription = splitSentenceByCharactersLimit(
					FirstSplittedDescription.join(' '),
					ConvenyanceOf2And3CharacterLimit,
				);
			}

			if (
				(!_formData?.convenyanceOf || _formData?.convenyanceOf?.length === 0) &&
				splittedDescription.length >= 1
			) {
				_formData.convenyanceOf = splittedDescription[0];
			}

			if (
				(!_formData?.convenyanceOf2 ||
					_formData?.convenyanceOf2?.length === 0) &&
				SecondSplittedDescription.length >= 1
			) {
				_formData.convenyanceOf2 = SecondSplittedDescription.join(' ');
			}
		}

		if (
			(!_formData?.by || _formData?.by?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.by = joinNames(
				otherParties.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			(!_formData?.to || _formData?.to?.length === 0) &&
			matterClients?.length > 0
		) {
			_formData.to = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (
			((!_formData?.moniesPaid || _formData?.moniesPaid?.length === 0) &&
				!lttCalculated?.tax?.moniesPaid &&
				typeof lttCalculated?.nsp === 'number') ||
			(lttCalculated?.tax?.moniesPaid &&
				typeof lttCalculated?.tax?.moniesPaid === 'number')
		) {
			_formData.moniesPaid = formatCentsToDollars(
				lttCalculated?.tax?.moniesPaid ?? lttCalculated?.nsp,
				false,
			);
		}

		if (
			(!_formData?.assumedMortgage ||
				_formData?.assumedMortgage?.length === 0) &&
			typeof lttCalculated?.tax?.assumedMortgage === 'number'
		) {
			_formData.assumedMortgage = formatCentsToDollars(
				lttCalculated?.tax?.assumedMortgage,
				false,
			);
		}

		if (
			(!_formData?.givenBackToVendor ||
				_formData?.givenBackToVendor?.length === 0) &&
			typeof lttCalculated?.tax?.returnedToVendor === 'number'
		) {
			_formData.givenBackToVendor = formatCentsToDollars(
				lttCalculated?.tax?.returnedToVendor,
				false,
			);
		}

		if (
			(!_formData?.propertyTransferredInExchange ||
				_formData?.propertyTransferredInExchange?.length === 0) &&
			typeof lttCalculated?.tax?.propertyTransferred === 'number'
		) {
			_formData.propertyTransferredInExchange = formatCentsToDollars(
				lttCalculated?.tax?.propertyTransferred,
				false,
			);
		}

		if (
			(!_formData?.otherConsiderationsSubjectToTax ||
				_formData?.otherConsiderationsSubjectToTax?.length === 0) &&
			typeof lttCalculated?.tax?.considerations === 'number'
		) {
			_formData.otherConsiderationsSubjectToTax = formatCentsToDollars(
				lttCalculated?.tax?.considerations,
				false,
			);
		}

		if (
			!_formData?.fairMarketValue ||
			_formData?.fairMarketValue?.length === 0
		) {
			_formData.fairMarketValue = 'NIL';
		}

		if (
			(!_formData?.totalConsiderationValue ||
				_formData?.totalConsiderationValue?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.totalConsiderationValue = formatCentsToDollars(
				lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (lttCalculated?.tax?.rate && lttCalculated?.subjectToLtt > 40000000) {
			if (lttCalculated?.tax.rate === LTTRate.COMMERCIAL) {
				if (
					!_formData?.conveyanceCheckbox ||
					_formData?.conveyanceCheckbox?.length === 0
				) {
					_formData.conveyanceCheckbox = 'Yes';
					_formData.conveyanceCheckbox2 = 'Off';
				}
			}

			if (lttCalculated?.tax.rate === LTTRate.RESIDENTIAL) {
				if (
					!_formData?.conveyanceCheckbox2 ||
					_formData?.conveyanceCheckbox2?.length === 0
				) {
					_formData.conveyanceCheckbox2 = 'Yes';
				}
			}

			if (
				lttCalculated?.tax.rate === LTTRate.APPORTION &&
				typeof lttCalculated?.tax?.residenceValue === 'number'
			) {
				if (
					!_formData?.conveyanceCheckbox2 ||
					_formData?.conveyanceCheckbox2?.length === 0
				) {
					_formData.conveyanceCheckbox2 = 'No';
				}

				if (
					!_formData?.singleFamilyConsiderationValue ||
					_formData?.singleFamilyConsiderationValue?.length === 0
				) {
					_formData.singleFamilyConsiderationValue = formatCentsToDollars(
						lttCalculated?.tax?.residenceValue,
						false,
					);
				}
			}
		}

		if (
			(!_formData?.conveyanceCheckbox ||
				_formData?.conveyanceCheckbox?.length === 0) &&
			typeof lttCalculated?.subjectToLtt === 'number'
		) {
			_formData.totalConsiderationValue = formatCentsToDollars(
				lttCalculated?.subjectToLtt,
				false,
			);
		}

		if (
			(!_formData?.swornBefore || _formData?.swornBefore?.length === 0) &&
			businessAddress?.city
		) {
			_formData.swornBefore = `City of ${businessAddress?.city}`;
		}

		if (
			(!_formData?.swornBefore2 || _formData?.swornBefore2?.length === 0) &&
			businessAddress?.province
		) {
			_formData.swornBefore2 = `${getRegionType(businessAddress?.province, businessAddress?.country)} of ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (matter?.closingDate) {
			const date = new Date(matter?.closingDate);
			if (!_formData?.month || _formData?.month?.length === 0) {
				_formData.month = format(date, 'MMMM');
			}

			if (!_formData?.day || _formData?.day?.length === 0) {
				_formData.day = format(date, 'dd');
			}

			if (!_formData?.year || _formData?.year?.length === 0) {
				_formData.year = format(date, 'yy');
			}
		}

		if (
			(!_formData?.addressOfProperty ||
				_formData?.addressOfProperty?.length === 0) &&
			propertyAddress?.lines2?.line1
		) {
			_formData.addressOfProperty = propertyAddress?.lines2?.line1 ?? '';
		}

		if (
			(!_formData?.addressOfProperty2 ||
				_formData?.addressOfProperty2?.length === 0) &&
			propertyAddress?.lines2?.line2
		) {
			_formData.addressOfProperty2 = propertyAddress?.lines2?.line2 ?? '';
		}

		if (
			(!_formData?.transfereeAddressForService ||
				_formData?.transfereeAddressForService?.length === 0) &&
			borrowerServiceAddress?.lines2?.line1
		) {
			_formData.transfereeAddressForService =
				borrowerServiceAddress?.lines2?.line1 ?? '';
		}

		if (
			(!_formData?.transfereeAddressForService2 ||
				_formData?.transfereeAddressForService2?.length === 0) &&
			borrowerServiceAddress?.lines2?.line2
		) {
			_formData.transfereeAddressForService2 =
				borrowerServiceAddress?.lines2?.line2 ?? '';
		}

		if (
			(!_formData?.propertyIdentifier ||
				_formData?.propertyIdentifier?.length === 0) &&
			matterTitles
		) {
			_formData.propertyIdentifier = listParcelsPin(matterTitles);
		}

		if (
			(!_formData?.nameAndAddressOfEachTransferee ||
				_formData?.nameAndAddressOfEachTransferee?.length === 0) &&
			matter?.lawyerAssigned
		) {
			_formData.nameAndAddressOfEachTransferee = matter?.lawyerAssigned?.name;
		}

		if (
			(!_formData?.nameAndAddressOfEachTransferee2 ||
				_formData?.nameAndAddressOfEachTransferee2?.length === 0) &&
			matter?.number
		) {
			_formData.nameAndAddressOfEachTransferee2 = `${addressString(businessAddress)} - [File ${matter?.number ?? ''}]`;
		}

		return _formData;
	}

	MapValues(): Partial<MLTT_AFFIDAVIT_FORM> {
		return {};
	}
}

const Mappings: MLTT_AFFIDAVIT_FORM = {
	convenyanceOf: 'Text1',
	convenyanceOf2: 'Text2',

	by: 'Text3',
	to: 'Text4',
	to2: 'cc099',

	checkboxA: 'CC007',
	checkboxB: 'CC008',
	checkboxC: 'CC009',
	checkboxCAuthorized: 'Text6',
	checkboxD: 'CC013',
	checkboxDTransferee: 'Text9',
	checkboxDSpouse: 'Text10',
	checkboxE: 'CC014',
	checkboxESpouse: 'Text11',
	checkboxE1: 'Check Box2',
	checkboxE2: 'Check Box3',

	moniesPaid: 'CC015',
	assumedMortgage: 'CC016',
	givenBackToVendor: 'CC017',
	propertyTransferredInExchange: 'CC018',
	otherConsiderationsSubjectToTax: 'CC019',
	fairMarketValue: 'CC020',
	totalConsiderationValue: 'CC021',

	conveyanceCheckbox: 'cc59',
	conveyanceCheckbox2: 'cc055',

	singleFamilyConsiderationValue: 'cc056',
	landsUseFor: 'cc057',

	landSubjectToEncumbrance: 'Check Box4',

	remarks: 'CC024',
	remarks2: 'Text25',

	swornBefore: 'Text26',
	swornBefore2: 'Text27',

	day: 'Text28',
	month: 'Text29',
	year: 'Text30',

	signatureName: 'Text32',

	addressOfProperty: 'Text33',
	addressOfProperty2: 'Text34',

	propertyIdentifier: 'Text35',
	assessmentRollNumber: 'Text36',

	transfereeAddressForService: 'Text37',
	transfereeAddressForService2: 'Text38',

	nameAndAddressOfEachTransferee: 'Text39',
	nameAndAddressOfEachTransferee2: 'Text40',
};

export interface MLTT_AFFIDAVIT_FORM {
	convenyanceOf: string;
	convenyanceOf2: string;

	by: string;
	to: string;
	to2: string;

	checkboxA: string;
	checkboxB: string;
	checkboxC: string;
	checkboxCAuthorized: string;
	checkboxD: string;
	checkboxDTransferee: string;
	checkboxDSpouse: string;
	checkboxE: string;
	checkboxESpouse: string;
	checkboxE1: string;
	checkboxE2: string;

	moniesPaid: string;
	assumedMortgage: string;
	givenBackToVendor: string;
	propertyTransferredInExchange: string;
	otherConsiderationsSubjectToTax: string;
	fairMarketValue: string;
	totalConsiderationValue: string;

	conveyanceCheckbox: string;
	conveyanceCheckbox2: string;

	singleFamilyConsiderationValue: string;
	landsUseFor: string;

	landSubjectToEncumbrance: string;

	remarks: string;
	remarks2: string;

	swornBefore: string;
	swornBefore2: string;

	day: string;
	month: string;
	year: string;

	signatureName: string;

	addressOfProperty: string;
	addressOfProperty2: string;

	propertyIdentifier: string;
	assessmentRollNumber: string;

	transfereeAddressForService: string;
	transfereeAddressForService2: string;
	nameAndAddressOfEachTransferee: string;
	nameAndAddressOfEachTransferee2: string;
}
